import type { Gender } from "./strings";

// French text for the drawings, laid over the English data rather than replacing
// it: `src/data/` stays the single source of geometry, and anything missing from
// these tables simply shows its English original.
//
// GENERATED by scratchpad/gen-overlay.mjs from the translation tables — but it
// is checked in and safe to hand-edit; the generator only exists so the first
// bulk pass didn't have to be typed out by hand.
//
// Hints are keyed by their EXACT English string, not by (drawing, step index).
// The same line recurs across a subject's Easy/Medium/Harder variants and right
// across collections ("Now color him all in!"), so keying by the English text
// translates every occurrence once and stops them drifting apart.
//
// The "write the name" finale is deliberately NOT in HINTS_FR. It is redrawn
// from the French name (see localize.ts), because its hint and its letter
// strokes have to agree — a French label above English letters would be worse
// than leaving the drawing in English.

export interface NameFr {
  name: string;
  /** Drives "un"/"une" in every sentence that names this drawing. */
  gender: Gender;
  /** Gender of this drawing's `title`, when it has one. A flag's heading reads
   *  "un drapeau français" — masculine — even though la France is feminine. */
  titleGender?: Gender;
  /** The word drawn in the "write the name" step, when it should differ from
   *  `name` (the Rain Cloud writes just "NUAGE"). Defaults to `name`. */
  written?: string;
  /** Greek only: the accusative-case phrase (article + noun) that follows
   *  "Πώς να ζωγραφίσεις …" — "έναν σκύλο", "μια γάτα", "ένα δέντρο". Greek
   *  nouns decline, so the heading needs a different form than the nominative
   *  `name` shown on the card. French/English leave this undefined. */
  accusative?: string;
}

/** Drawing id → its French name. */
export const NAMES_FR: Record<string, NameFr> = {
  "bear": { name: "Ours", gender: "m" },
  "bunny": { name: "Lapin", gender: "m" },
  "butterfly": { name: "Papillon", gender: "m" },
  "butterfly-10": { name: "Papillon", gender: "m" },
  "butterfly-7": { name: "Papillon", gender: "m" },
  "car": { name: "Voiture", gender: "f" },
  "car-10": { name: "Voiture", gender: "f" },
  "car-7": { name: "Voiture", gender: "f" },
  "cat": { name: "Chat", gender: "m" },
  "cat-10": { name: "Chat", gender: "m" },
  "cat-7": { name: "Chat", gender: "m" },
  "cloud": { name: "Nuage de pluie", gender: "m", written: "Nuage" },
  "cloud-10": { name: "Nuage de pluie", gender: "m", written: "Nuage" },
  "cloud-7": { name: "Nuage de pluie", gender: "m", written: "Nuage" },
  "cow": { name: "Vache", gender: "f" },
  "cubist-face": { name: "Visage cubiste", gender: "m" },
  "dinosaur": { name: "Dinosaure", gender: "m" },
  "dinosaur-10": { name: "Dinosaure", gender: "m" },
  "dinosaur-7": { name: "Dinosaure", gender: "m" },
  "dinosaur-blue": { name: "Dinosaure", gender: "m" },
  "dog": { name: "Chien", gender: "m" },
  "dog-10": { name: "Chien", gender: "m" },
  "dog-7": { name: "Chien", gender: "m" },
  "duck": { name: "Canard", gender: "m" },
  "elephant": { name: "Éléphant", gender: "m" },
  "family": { name: "Famille", gender: "f" },
  "family-10": { name: "Famille", gender: "f" },
  "family-7": { name: "Famille", gender: "f" },
  "fish": { name: "Poisson", gender: "m" },
  "fish-10": { name: "Poisson", gender: "m" },
  "fish-7": { name: "Poisson", gender: "m" },
  "flag-algeria": { name: "Algérie", gender: "f", titleGender: "m" },
  "flag-argentina": { name: "Argentine", gender: "f", titleGender: "m" },
  "flag-australia": { name: "Australie", gender: "f", titleGender: "m" },
  "flag-austria": { name: "Autriche", gender: "f", titleGender: "m" },
  "flag-belgium": { name: "Belgique", gender: "f", titleGender: "m" },
  "flag-bosnia-and-herzegovina": { name: "Bosnie-Herzégovine", gender: "f", titleGender: "m" },
  "flag-brazil": { name: "Brésil", gender: "m", titleGender: "m" },
  "flag-canada": { name: "Canada", gender: "m", titleGender: "m" },
  "flag-cape-verde": { name: "Cap-Vert", gender: "m", titleGender: "m" },
  "flag-china": { name: "Chine", gender: "f", titleGender: "m" },
  "flag-colombia": { name: "Colombie", gender: "f", titleGender: "m" },
  "flag-croatia": { name: "Croatie", gender: "f", titleGender: "m" },
  "flag-curacao": { name: "Curaçao", gender: "m", titleGender: "m" },
  "flag-czech-republic": { name: "République tchèque", gender: "f", titleGender: "m" },
  "flag-denmark": { name: "Danemark", gender: "m", titleGender: "m" },
  "flag-dr-congo": { name: "RD Congo", gender: "m", titleGender: "m" },
  "flag-ecuador": { name: "Équateur", gender: "m", titleGender: "m" },
  "flag-egypt": { name: "Égypte", gender: "f", titleGender: "m" },
  "flag-england": { name: "Angleterre", gender: "f", titleGender: "m" },
  "flag-finland": { name: "Finlande", gender: "f", titleGender: "m" },
  "flag-france": { name: "France", gender: "f", titleGender: "m" },
  "flag-germany": { name: "Allemagne", gender: "f", titleGender: "m" },
  "flag-ghana": { name: "Ghana", gender: "m", titleGender: "m" },
  "flag-greece": { name: "Grèce", gender: "f", titleGender: "m" },
  "flag-haiti": { name: "Haïti", gender: "f", titleGender: "m" },
  "flag-india": { name: "Inde", gender: "f", titleGender: "m" },
  "flag-iran": { name: "Iran", gender: "m", titleGender: "m" },
  "flag-iraq": { name: "Irak", gender: "m", titleGender: "m" },
  "flag-ireland": { name: "Irlande", gender: "f", titleGender: "m" },
  "flag-italy": { name: "Italie", gender: "f", titleGender: "m" },
  "flag-ivory-coast": { name: "Côte d'Ivoire", gender: "f", titleGender: "m" },
  "flag-japan": { name: "Japon", gender: "m", titleGender: "m" },
  "flag-jordan": { name: "Jordanie", gender: "f", titleGender: "m" },
  "flag-mexico": { name: "Mexique", gender: "m", titleGender: "m" },
  "flag-morocco": { name: "Maroc", gender: "m", titleGender: "m" },
  "flag-netherlands": { name: "Pays-Bas", gender: "m", titleGender: "m" },
  "flag-new-zealand": { name: "Nouvelle-Zélande", gender: "f", titleGender: "m" },
  "flag-norway": { name: "Norvège", gender: "f", titleGender: "m" },
  "flag-panama": { name: "Panama", gender: "m", titleGender: "m" },
  "flag-paraguay": { name: "Paraguay", gender: "m", titleGender: "m" },
  "flag-portugal": { name: "Portugal", gender: "m", titleGender: "m" },
  "flag-qatar": { name: "Qatar", gender: "m", titleGender: "m" },
  "flag-saudi-arabia": { name: "Arabie saoudite", gender: "f", titleGender: "m" },
  "flag-scotland": { name: "Écosse", gender: "f", titleGender: "m" },
  "flag-senegal": { name: "Sénégal", gender: "m", titleGender: "m" },
  "flag-singapore": { name: "Singapour", gender: "m", titleGender: "m" },
  "flag-south-africa": { name: "Afrique du Sud", gender: "f", titleGender: "m" },
  "flag-south-korea": { name: "Corée du Sud", gender: "f", titleGender: "m" },
  "flag-spain": { name: "Espagne", gender: "f", titleGender: "m" },
  "flag-sweden": { name: "Suède", gender: "f", titleGender: "m" },
  "flag-switzerland": { name: "Suisse", gender: "f", titleGender: "m" },
  "flag-tunisia": { name: "Tunisie", gender: "f", titleGender: "m" },
  "flag-turkey": { name: "Turquie", gender: "f", titleGender: "m" },
  "flag-united-arab-emirates": { name: "Émirats arabes unis", gender: "m", titleGender: "m" },
  "flag-united-kingdom": { name: "Royaume-Uni", gender: "m", titleGender: "m" },
  "flag-united-states": { name: "États-Unis", gender: "m", titleGender: "m" },
  "flag-uruguay": { name: "Uruguay", gender: "m", titleGender: "m" },
  "flag-uzbekistan": { name: "Ouzbékistan", gender: "m", titleGender: "m" },
  "flower": { name: "Fleur", gender: "f" },
  "flower-10": { name: "Fleur", gender: "f" },
  "flower-7": { name: "Fleur", gender: "f" },
  "fox": { name: "Renard", gender: "m" },
  "frog": { name: "Grenouille", gender: "f" },
  "great-wave": { name: "Grande Vague", gender: "f" },
  "hamster": { name: "Hamster", gender: "m" },
  "heart": { name: "Cœur", gender: "m" },
  "heart-10": { name: "Cœur", gender: "m" },
  "heart-7": { name: "Cœur", gender: "m" },
  "hedgehog": { name: "Hérisson", gender: "m" },
  "hedgehog-10": { name: "Hérisson", gender: "m" },
  "hedgehog-7": { name: "Hérisson", gender: "m" },
  "heromask": { name: "Super-Stormy", gender: "m" },
  "heromask-10": { name: "Super-Stormy", gender: "m" },
  "heromask-7": { name: "Super-Stormy", gender: "m" },
  "horse": { name: "Cheval", gender: "m" },
  "horse-10": { name: "Cheval", gender: "m" },
  "horse-7": { name: "Cheval", gender: "m" },
  "house": { name: "Maison", gender: "f" },
  "house-10": { name: "Maison", gender: "f" },
  "house-7": { name: "Maison", gender: "f" },
  "ice-cream": { name: "Glace", gender: "f" },
  "jersey": { name: "Maillot", gender: "m" },
  "unicorn-10": { name: "Licorne", gender: "f" },
  "lion": { name: "Lion", gender: "m" },
  "mouse": { name: "Souris", gender: "f" },
  "penguin": { name: "Pingouin", gender: "m" },
  "picasso-portrait": { name: "Portrait à la Picasso", gender: "m" },
  "pig": { name: "Cochon", gender: "m" },
  "popstar": { name: "Pop Star", gender: "f" },
  "popstar-10": { name: "Pop Star", gender: "f" },
  "popstar-7": { name: "Pop Star", gender: "f" },
  "princess": { name: "Princesse", gender: "f" },
  "princess-10": { name: "Princesse", gender: "f" },
  "princess-7": { name: "Princesse", gender: "f" },
  "rainbow": { name: "Arc-en-ciel", gender: "m" },
  "rainbow-10": { name: "Arc-en-ciel", gender: "m" },
  "rainbow-7": { name: "Arc-en-ciel", gender: "m" },
  "shape-circle": { name: "Cercle", gender: "m" },
  "shape-crescent": { name: "Croissant", gender: "m" },
  "shape-diamond": { name: "Losange", gender: "m" },
  "shape-heart": { name: "Cœur", gender: "m" },
  "shape-hexagon": { name: "Hexagone", gender: "m" },
  "shape-octagon": { name: "Octogone", gender: "m" },
  "shape-oval": { name: "Ovale", gender: "m" },
  "shape-parallelogram": { name: "Parallélogramme", gender: "m" },
  "shape-pentagon": { name: "Pentagone", gender: "m" },
  "shape-rectangle": { name: "Rectangle", gender: "m" },
  "shape-square": { name: "Carré", gender: "m" },
  "shape-star": { name: "Étoile", gender: "f" },
  "shape-trapezium": { name: "Trapèze", gender: "m" },
  "shape-triangle": { name: "Triangle", gender: "m" },
  "snail": { name: "Escargot", gender: "m" },
  "star": { name: "Étoile", gender: "f" },
  "star-10": { name: "Étoile", gender: "f" },
  "star-7": { name: "Étoile", gender: "f" },
  "starry-night": { name: "Nuit étoilée", gender: "f" },
  "sun": { name: "Soleil", gender: "m" },
  "sun-10": { name: "Soleil", gender: "m" },
  "sun-7": { name: "Soleil", gender: "m" },
  "tree": { name: "Arbre", gender: "m" },
  "tree-10": { name: "Arbre", gender: "m" },
  "tree-7": { name: "Arbre", gender: "m" },
  "whale": { name: "Baleine", gender: "f" },
};

/** English `title` → French. Lower-case: it lands inside "Comment dessiner un …". */
export const TITLES_FR: Record<string, string> = {
  "Algerian flag":
    "drapeau algérien",
  "American flag":
    "drapeau américain",
  "Argentine flag":
    "drapeau argentin",
  "Australian flag":
    "drapeau australien",
  "Austrian flag":
    "drapeau autrichien",
  "Belgian flag":
    "drapeau belge",
  "Bosnian flag":
    "drapeau bosnien",
  "Brazilian flag":
    "drapeau brésilien",
  "British flag":
    "drapeau britannique",
  "Canadian flag":
    "drapeau canadien",
  "Cape Verdean flag":
    "drapeau cap-verdien",
  "Chinese flag":
    "drapeau chinois",
  "Colombian flag":
    "drapeau colombien",
  "Congolese flag":
    "drapeau congolais",
  "Croatian flag":
    "drapeau croate",
  "Curaçao flag":
    "drapeau de Curaçao",
  "Czech flag":
    "drapeau tchèque",
  "Danish flag":
    "drapeau danois",
  "Dutch flag":
    "drapeau néerlandais",
  "Ecuadorian flag":
    "drapeau équatorien",
  "Egyptian flag":
    "drapeau égyptien",
  "Emirati flag":
    "drapeau émirati",
  "English flag":
    "drapeau anglais",
  "Finnish flag":
    "drapeau finlandais",
  "French flag":
    "drapeau français",
  "German flag":
    "drapeau allemand",
  "Ghanaian flag":
    "drapeau ghanéen",
  "Greek flag":
    "drapeau grec",
  "Haitian flag":
    "drapeau haïtien",
  "Indian flag":
    "drapeau indien",
  "Iranian flag":
    "drapeau iranien",
  "Iraqi flag":
    "drapeau irakien",
  "Irish flag":
    "drapeau irlandais",
  "Italian flag":
    "drapeau italien",
  "Ivorian flag":
    "drapeau ivoirien",
  "Japanese flag":
    "drapeau japonais",
  "Jordanian flag":
    "drapeau jordanien",
  "Mexican flag":
    "drapeau mexicain",
  "Moroccan flag":
    "drapeau marocain",
  "New Zealand flag":
    "drapeau néo-zélandais",
  "Norwegian flag":
    "drapeau norvégien",
  "Panamanian flag":
    "drapeau panaméen",
  "Paraguayan flag":
    "drapeau paraguayen",
  "Portuguese flag":
    "drapeau portugais",
  "Qatari flag":
    "drapeau qatarien",
  "Saudi flag":
    "drapeau saoudien",
  "Scottish flag":
    "drapeau écossais",
  "Senegalese flag":
    "drapeau sénégalais",
  "Singaporean flag":
    "drapeau singapourien",
  "South African flag":
    "drapeau sud-africain",
  "South Korean flag":
    "drapeau sud-coréen",
  "Spanish flag":
    "drapeau espagnol",
  "Swedish flag":
    "drapeau suédois",
  "Swiss flag":
    "drapeau suisse",
  "Tunisian flag":
    "drapeau tunisien",
  "Turkish flag":
    "drapeau turc",
  "Uruguayan flag":
    "drapeau uruguayen",
  "Uzbek flag":
    "drapeau ouzbek",
};

/** English `fact` → French. Shown on the celebration screen; never spoken. */
export const FACTS_FR: Record<string, string> = {
  "A circle is perfectly round — it has no corners at all! The sun and the moon are circles. ☀️":
    "Un cercle est parfaitement rond — il n'a aucun coin ! Le soleil et la lune sont des cercles. ☀️",
  "A crescent is the curvy shape the moon makes at night! 🌙":
    "Le croissant est la forme courbe que dessine la lune, la nuit ! 🌙",
  "A diamond is a square balancing on one of its corners! 💎":
    "Un losange, c'est un carré en équilibre sur un de ses coins ! 💎",
  "A heart is the shape we use to show love! 💕":
    "Le cœur est la forme qu'on dessine pour dire qu'on aime ! 💕",
  "A hexagon has 6 sides — bees build their honeycomb out of hexagons! 🐝":
    "Un hexagone a 6 côtés — les abeilles construisent leurs rayons avec des hexagones ! 🐝",
  "A parallelogram is like a rectangle that leaned over to one side!":
    "Un parallélogramme, c'est un rectangle qui s'est penché sur le côté !",
  "A pentagon has 5 straight sides — a little house is shaped a bit like one! 🏠":
    "Un pentagone a 5 côtés droits — une petite maison lui ressemble un peu ! 🏠",
  "A rectangle has 4 corners, with 2 long sides and 2 short sides — like a door! 🚪":
    "Un rectangle a 4 coins, avec 2 longs côtés et 2 côtés courts — comme une porte ! 🚪",
  "A square has 4 sides that are all exactly the same length!":
    "Un carré a 4 côtés qui font tous exactement la même longueur !",
  "A star has 5 points that twinkle in the night sky! ✨":
    "Une étoile a 5 pointes qui scintillent dans le ciel de la nuit ! ✨",
  "A trapezium has 4 sides, with a flat top and a flat bottom that aren't the same width!":
    "Un trapèze a 4 côtés, avec un dessus plat et un dessous plat qui n'ont pas la même largeur !",
  "A triangle has 3 sides and 3 pointy corners!":
    "Un triangle a 3 côtés et 3 coins pointus !",
  "Algeria's flag has a red moon and star.":
    "Le drapeau de l'Algérie a une lune et une étoile rouges.",
  "An octagon has 8 sides — a red STOP sign is an octagon! 🛑":
    "Un octogone a 8 côtés — un panneau STOP rouge est un octogone ! 🛑",
  "An oval is like a circle that got gently squished — an egg is an oval! 🥚":
    "Un ovale, c'est un cercle qu'on a doucement aplati — un œuf est un ovale ! 🥚",
  "Argentina's flag has a smiling golden sun.":
    "Le drapeau de l'Argentine a un soleil doré qui sourit.",
  "Australia's flag shows the Southern Cross stars.":
    "Le drapeau de l'Australie montre les étoiles de la Croix du Sud.",
  "Austria's flag is red, white, red.":
    "Le drapeau de l'Autriche est rouge, blanc, rouge.",
  "Belgium's flag is black, yellow and red.":
    "Le drapeau de la Belgique est noir, jaune et rouge.",
  "Bosnia's flag has a yellow triangle and white stars.":
    "Le drapeau de la Bosnie a un triangle jaune et des étoiles blanches.",
  "Brazil's flag has a starry blue globe.":
    "Le drapeau du Brésil a un globe bleu étoilé.",
  "Canada's flag shows a red maple leaf.":
    "Le drapeau du Canada montre une feuille d'érable rouge.",
  "Cape Verde's flag has ten yellow stars in a circle.":
    "Le drapeau du Cap-Vert a dix étoiles jaunes en cercle.",
  "China's flag has one big star and four small ones.":
    "Le drapeau de la Chine a une grande étoile et quatre petites.",
  "Colombia's flag is yellow, blue and red.":
    "Le drapeau de la Colombie est jaune, bleu et rouge.",
  "Croatia's flag has a red-and-white checkerboard.":
    "Le drapeau de la Croatie a un damier rouge et blanc.",
  "Curaçao's flag has two white stars.":
    "Le drapeau de Curaçao a deux étoiles blanches.",
  "DR Congo's flag has a yellow star and a red stripe.":
    "Le drapeau de la RD Congo a une étoile jaune et une bande rouge.",
  "Denmark's flag is the oldest in the world!":
    "Le drapeau du Danemark est le plus vieux du monde !",
  "Ecuador's flag has a condor on a shield.":
    "Le drapeau de l'Équateur a un condor sur un bouclier.",
  "Egypt's flag has a golden eagle.":
    "Le drapeau de l'Égypte a un aigle doré.",
  "England's flag is a red cross.":
    "Le drapeau de l'Angleterre est une croix rouge.",
  "Finland's flag has a blue cross.":
    "Le drapeau de la Finlande a une croix bleue.",
  "France's flag is called the Tricolore.":
    "Le drapeau de la France s'appelle le Tricolore.",
  "Germany's flag is black, red and gold.":
    "Le drapeau de l'Allemagne est noir, rouge et or.",
  "Ghana's flag has a black star.":
    "Le drapeau du Ghana a une étoile noire.",
  "Greece's flag has nine blue and white stripes.":
    "Le drapeau de la Grèce a neuf bandes bleues et blanches.",
  "Haiti's flag has a palm tree in the middle.":
    "Le drapeau d'Haïti a un palmier au milieu.",
  "Hokusai made this in Japan almost 200 years ago — it's printed from carved wood!":
    "Hokusai a fait ceci au Japon il y a presque 200 ans — c'est imprimé à partir de bois gravé !",
  "In real life, Switzerland's flag is a perfect square!":
    "En vrai, le drapeau de la Suisse est un carré parfait !",
  "India's flag has a blue wheel in the middle.":
    "Le drapeau de l'Inde a une roue bleue au milieu.",
  "Iran's flag has a red emblem in the middle.":
    "Le drapeau de l'Iran a un emblème rouge au milieu.",
  "Iraq's flag has green writing in the middle.":
    "Le drapeau de l'Irak a une écriture verte au milieu.",
  "Ireland's flag is green, white and orange.":
    "Le drapeau de l'Irlande est vert, blanc et orange.",
  "Italy's flag is green, white and red.":
    "Le drapeau de l'Italie est vert, blanc et rouge.",
  "Ivory Coast's flag is orange, white and green.":
    "Le drapeau de la Côte d'Ivoire est orange, blanc et vert.",
  "Japan's flag shows the rising sun.":
    "Le drapeau du Japon montre le soleil levant.",
  "Jordan's flag has a white star with seven points.":
    "Le drapeau de la Jordanie a une étoile blanche à sept branches.",
  "Mexico's flag has an eagle on a cactus.":
    "Le drapeau du Mexique a un aigle sur un cactus.",
  "Morocco's flag has a green star.":
    "Le drapeau du Maroc a une étoile verte.",
  "New Zealand's flag has four red stars.":
    "Le drapeau de la Nouvelle-Zélande a quatre étoiles rouges.",
  "Norway's flag has a cross inside a cross.":
    "Le drapeau de la Norvège a une croix dans une croix.",
  "Panama's flag has a blue star and a red star.":
    "Le drapeau du Panama a une étoile bleue et une étoile rouge.",
  "Paraguay's flag has a star in the middle.":
    "Le drapeau du Paraguay a une étoile au milieu.",
  "Picasso drew faces from the front and the side at the same time — that's Cubism!":
    "Picasso dessinait les visages de face et de profil en même temps — c'est le cubisme !",
  "Picasso often showed a face from the front and the side at the same time — two views in one!":
    "Picasso montrait souvent un visage de face et de profil en même temps — deux vues en une !",
  "Portugal's flag has a golden sphere and a shield.":
    "Le drapeau du Portugal a une sphère dorée et un bouclier.",
  "Qatar's flag has nine white points.":
    "Le drapeau du Qatar a neuf pointes blanches.",
  "Saudi Arabia's flag has a sword.":
    "Le drapeau de l'Arabie saoudite a une épée.",
  "Scotland's flag is a white X on blue.":
    "Le drapeau de l'Écosse est un X blanc sur du bleu.",
  "Senegal's flag has a green star.":
    "Le drapeau du Sénégal a une étoile verte.",
  "Singapore's flag has a moon and five stars.":
    "Le drapeau de Singapour a une lune et cinq étoiles.",
  "South Africa's flag has six colours in a Y shape.":
    "Le drapeau de l'Afrique du Sud a six couleurs en forme de Y.",
  "South Korea's flag has a red-and-blue circle.":
    "Le drapeau de la Corée du Sud a un cercle rouge et bleu.",
  "Spain's flag has a crown and a shield.":
    "Le drapeau de l'Espagne a une couronne et un bouclier.",
  "Sweden's flag has a yellow cross.":
    "Le drapeau de la Suède a une croix jaune.",
  "The Czech flag has a blue triangle.":
    "Le drapeau de la République tchèque a un triangle bleu.",
  "The Netherlands flag is red, white and blue.":
    "Le drapeau des Pays-Bas est rouge, blanc et bleu.",
  "The UAE flag has four colours.":
    "Le drapeau des Émirats arabes unis a quatre couleurs.",
  "The USA flag has 13 stripes and 50 stars!":
    "Le drapeau des États-Unis a 13 bandes et 50 étoiles !",
  "The Union Jack joins three crosses together.":
    "L'Union Jack réunit trois croix ensemble.",
  "Tunisia's flag has a red moon and star in a white circle.":
    "Le drapeau de la Tunisie a une lune et une étoile rouges dans un cercle blanc.",
  "Turkey's flag has a white moon and star.":
    "Le drapeau de la Turquie a une lune et une étoile blanches.",
  "Uruguay's flag has a golden sun and nine stripes.":
    "Le drapeau de l'Uruguay a un soleil doré et neuf bandes.",
  "Uzbekistan's flag has a moon and stars.":
    "Le drapeau de l'Ouzbékistan a une lune et des étoiles.",
  "Vincent van Gogh painted Starry Night from his window — with big swirly brushstrokes!":
    "Vincent van Gogh a peint La Nuit étoilée depuis sa fenêtre — avec de grands coups de pinceau tourbillonnants !",
};

/** English hint → French hint. Keyed by the exact English string. */
export const HINTS_FR: Record<string, string> = {
  "Add 10 little stars in a circle ⭐":
    "Ajoute 10 petites étoiles en cercle ⭐",
  "Add 13 stripes across":
    "Ajoute 13 bandes en travers",
  "Add 3 stripes across":
    "Ajoute 3 bandes en travers",
  "Add 3 stripes across the rest":
    "Ajoute 3 bandes sur le reste",
  "Add 3 stripes up and down":
    "Ajoute 3 bandes de haut en bas",
  "Add 50 little stars ⭐":
    "Ajoute 50 petites étoiles ⭐",
  "Add 9 stripes across":
    "Ajoute 9 bandes en travers",
  "Add a band across the bottom half":
    "Ajoute une bande sur la moitié du bas",
  "Add a big cloud of leaves 🌳":
    "Ajoute un grand nuage de feuilles 🌳",
  "Add a big flowing cape!":
    "Ajoute une grande cape qui flotte !",
  "Add a big friendly smile 😄":
    "Ajoute un grand sourire tout gentil 😄",
  "Add a big muzzle and a smile 🐽":
    "Ajoute un gros museau et un sourire 🐽",
  "Add a big singing smile 🎶":
    "Ajoute un grand sourire qui chante 🎶",
  "Add a big smile 😄":
    "Ajoute un grand sourire 😄",
  "Add a big snout 🐽":
    "Ajoute un gros groin 🐽",
  "Add a big zig-zag lightning bolt! ⚡":
    "Ajoute un grand éclair en zig-zag ! ⚡",
  "Add a box in the corner":
    "Ajoute un carré dans le coin",
  "Add a box in the corner for the stars":
    "Ajoute un carré dans le coin pour les étoiles",
  "Add a box in the corner for the sun":
    "Ajoute un carré dans le coin pour le soleil",
  "Add a brave smile":
    "Ajoute un sourire courageux",
  "Add a bright sun! ☀️":
    "Ajoute un beau soleil ! ☀️",
  "Add a buzzy bee 🐝":
    "Ajoute une abeille qui bzzz 🐝",
  "Add a collar with a name tag":
    "Ajoute un collier avec une étiquette",
  "Add a colourful umbrella ☂️":
    "Ajoute un parapluie tout coloré ☂️",
  "Add a crescent moon 🌙":
    "Ajoute un croissant de lune 🌙",
  "Add a crooked smile 🙂":
    "Ajoute un sourire de travers 🙂",
  "Add a cross — a tall line and a wide line ➕":
    "Ajoute une croix — un trait haut et un trait large ➕",
  "Add a cross in the box ➕":
    "Ajoute une croix dans le carré ➕",
  "Add a curly tail!":
    "Ajoute une queue en tire-bouchon !",
  "Add a curvy band 🎀":
    "Ajoute une bande ondulée 🎀",
  "Add a cute nose 👃":
    "Ajoute un petit nez tout mignon 👃",
  "Add a door, handle and headlight":
    "Ajoute une portière, une poignée et un phare",
  "Add a fat white cross in the middle ➕":
    "Ajoute une grosse croix blanche au milieu ➕",
  "Add a fin on top and bottom":
    "Ajoute une nageoire en haut et en bas",
  "Add a five-point star ⭐":
    "Ajoute une étoile à cinq branches ⭐",
  "Add a flowing cape!":
    "Ajoute une cape qui flotte !",
  "Add a fluffy cloud ☁️":
    "Ajoute un nuage tout doux ☁️",
  "Add a fluffy cloud at each end ☁️":
    "Ajoute un nuage tout doux à chaque bout ☁️",
  "Add a fluffy cloud on the left ☁️":
    "Ajoute un nuage tout doux à gauche ☁️",
  "Add a glowing moon and twinkly stars 🌙":
    "Ajoute une lune qui brille et des étoiles qui scintillent 🌙",
  "Add a happy eye 👀":
    "Ajoute un œil tout content 👀",
  "Add a hero belt":
    "Ajoute une ceinture de héros",
  "Add a line across the middle":
    "Ajoute un trait au milieu",
  "Add a line down":
    "Ajoute un trait vers le bas",
  "Add a line down for the red bar":
    "Ajoute un trait vers le bas pour la barre rouge",
  "Add a line down the middle":
    "Ajoute un trait au milieu, de haut en bas",
  "Add a line of little stars ⭐":
    "Ajoute une rangée de petites étoiles ⭐",
  "Add a little arm":
    "Ajoute un petit bras",
  "Add a little bird 🐦":
    "Ajoute un petit oiseau 🐦",
  "Add a little cluster of stars ⭐":
    "Ajoute un petit groupe d'étoiles ⭐",
  "Add a little feather tuft on top 🪶":
    "Ajoute une petite houppe de plumes sur la tête 🪶",
  "Add a little fence and flowers 🌷":
    "Ajoute une petite barrière et des fleurs 🌷",
  "Add a little nose 👃":
    "Ajoute un petit nez 👃",
  "Add a little orange beak 🔶":
    "Ajoute un petit bec orange 🔶",
  "Add a little tongue!":
    "Ajoute une petite langue !",
  "Add a little triangle nose 👃":
    "Ajoute un petit nez en triangle 👃",
  "Add a long horse head from the side":
    "Ajoute une longue tête de cheval de côté",
  "Add a long neck and a big head":
    "Ajoute un long cou et une grosse tête",
  "Add a long swishy tail":
    "Ajoute une longue queue qui balance",
  "Add a long trunk 🐘":
    "Ajoute une longue trompe 🐘",
  "Add a magic wand! ✨":
    "Ajoute une baguette magique ! ✨",
  "Add a microphone to sing into! 🎤":
    "Ajoute un micro pour chanter ! 🎤",
  "Add a microphone with a cord 🎤":
    "Ajoute un micro avec un fil 🎤",
  "Add a nose and a smile 👃":
    "Ajoute un nez et un sourire 👃",
  "Add a nose and a smile 😄":
    "Ajoute un nez et un sourire 😄",
  "Add a nose seen from the side 👃":
    "Ajoute un nez vu de côté 👃",
  "Add a pointy ear 👂":
    "Ajoute une oreille pointue 👂",
  "Add a rosy cheek and a smile 😊":
    "Ajoute une joue rose et un sourire 😊",
  "Add a round face on top 🟡":
    "Ajoute une tête toute ronde par-dessus 🟡",
  "Add a second row of spikes":
    "Ajoute une deuxième rangée de piquants",
  "Add a second row of spikes!":
    "Ajoute une deuxième rangée de piquants !",
  "Add a shiny highlight ✨":
    "Ajoute un reflet brillant ✨",
  "Add a shiny star on the shirt ⭐":
    "Ajoute une étoile brillante sur le maillot ⭐",
  "Add a side mirror and a puff of exhaust 💨":
    "Ajoute un rétroviseur et un nuage de fumée 💨",
  "Add a smile 😄":
    "Ajoute un sourire 😄",
  "Add a smile and rosy cheeks 😊":
    "Ajoute un sourire et des joues roses 😊",
  "Add a smile and whiskers 〰️":
    "Ajoute un sourire et des moustaches 〰️",
  "Add a smiley mouth and a forelock 😄":
    "Ajoute une bouche souriante et une mèche 😄",
  "Add a soft snout 🤎":
    "Ajoute un museau tout doux 🤎",
  "Add a soft white tummy 🤍":
    "Ajoute un ventre blanc tout doux 🤍",
  "Add a sparkly crown 👑":
    "Ajoute une couronne qui brille 👑",
  "Add a sparkly jewelled crown 👑":
    "Ajoute une couronne brillante avec des bijoux 👑",
  "Add a splashy puddle 💧":
    "Ajoute une flaque qui éclabousse 💧",
  "Add a splashy tail":
    "Ajoute une queue qui éclabousse",
  "Add a star ⭐":
    "Ajoute une étoile ⭐",
  "Add a star in the top corner ⭐":
    "Ajoute une étoile dans le coin en haut ⭐",
  "Add a star with seven points ⭐":
    "Ajoute une étoile à sept branches ⭐",
  "Add a swishy tail":
    "Ajoute une queue qui balance",
  "Add a thinner cross inside ➕":
    "Ajoute une croix plus fine à l'intérieur ➕",
  "Add a thinner stripe inside it":
    "Ajoute une bande plus fine à l'intérieur",
  "Add a third row of tiny spikes!":
    "Ajoute une troisième rangée de petits piquants !",
  "Add a triangle roof 🔺":
    "Ajoute un toit en triangle 🔺",
  "Add a wagging tail!":
    "Ajoute une queue qui remue !",
  "Add a water spout! 💦":
    "Ajoute un jet d'eau ! 💦",
  "Add a white snout in the middle":
    "Ajoute un museau blanc au milieu",
  "Add a wide orange beak 🟧":
    "Ajoute un large bec orange 🟧",
  "Add a wide stripe across the bottom":
    "Ajoute une large bande en bas",
  "Add a zig-zag lightning bolt! ⚡":
    "Ajoute un éclair en zig-zag ! ⚡",
  "Add a zig-zag line down ⚡":
    "Ajoute une ligne en zigzag ⚡",
  "Add an apple on its back! 🍎":
    "Ajoute une pomme sur son dos ! 🍎",
  "Add an eagle on top 🦅":
    "Ajoute un aigle au-dessus 🦅",
  "Add an eye and a happy smile 😊":
    "Ajoute un œil et un joli sourire 😊",
  "Add an eye and spots 😄":
    "Ajoute un œil et des taches 😄",
  "Add an eye, a nostril and a smile 👀":
    "Ajoute un œil, une narine et un sourire 👀",
  "Add another row of rain":
    "Ajoute une autre rangée de pluie",
  "Add arms and legs":
    "Ajoute des bras et des jambes",
  "Add bark lines on the trunk":
    "Ajoute des lignes d'écorce sur le tronc",
  "Add big round wheels! ⚫":
    "Ajoute de grosses roues rondes ! ⚫",
  "Add big sharp teeth! 🦷":
    "Ajoute de grosses dents pointues ! 🦷",
  "Add big spots on the wings":
    "Ajoute de grosses taches sur les ailes",
  "Add big swirls in the sky 🌀":
    "Ajoute de grands tourbillons dans le ciel 🌀",
  "Add bumpy plates along its back!":
    "Ajoute des plaques bosselées sur son dos !",
  "Add colorful lips 👄":
    "Ajoute des lèvres toutes colorées 👄",
  "Add cool sunglasses 😎":
    "Ajoute des lunettes de soleil trop cool 😎",
  "Add cool swooshy hair! 💇":
    "Ajoute une coiffure trop cool ! 💇",
  "Add cosy stripes on its body 🐈":
    "Ajoute de jolies rayures sur son corps 🐈",
  "Add curly smoke from the chimney 💨":
    "Ajoute de la fumée qui tourbillonne sur la cheminée 💨",
  "Add curvy scales 🐟":
    "Ajoute des écailles arrondies 🐟",
  "Add diagonal rays in the corners":
    "Ajoute des rayons en diagonale dans les coins",
  "Add eight petals all around! 🌸":
    "Ajoute 8 pétales tout autour ! 🌸",
  "Add even more rain! 🌧️":
    "Ajoute encore plus de pluie ! 🌧️",
  "Add eyes and a big smile 🎶":
    "Ajoute des yeux et un grand sourire 🎶",
  "Add eyes and eyebrows 👀":
    "Ajoute des yeux et des sourcils 👀",
  "Add fins on top and bottom":
    "Ajoute des nageoires en haut et en bas",
  "Add five little stars ⭐":
    "Ajoute cinq petites étoiles ⭐",
  "Add foamy claws on top ☁️":
    "Ajoute des griffes d'écume tout en haut ☁️",
  "Add four legs that join the body 🦵":
    "Ajoute 4 pattes qui rejoignent le corps 🦵",
  "Add four little feet 🦶":
    "Ajoute 4 petits pieds 🦶",
  "Add four little stars ✨":
    "Ajoute quatre petites étoiles ✨",
  "Add front legs and paws 🐾":
    "Ajoute les pattes avant 🐾",
  "Add lilac swirls too 💜":
    "Ajoute aussi des mèches lilas 💜",
  "Add little bars in the corners":
    "Ajoute des petites barres dans les coins",
  "Add little golden dots 💛":
    "Ajoute des petits points dorés 💛",
  "Add little Mount Fuji behind 🗻":
    "Ajoute le petit mont Fuji derrière 🗻",
  "Add little seeds in the middle":
    "Ajoute de petites graines au milieu",
  "Add little stars in the sky! ⭐":
    "Ajoute de petites étoiles dans le ciel ! ⭐",
  "Add more rays in the corners! 🌟":
    "Ajoute plus de rayons dans les coins ! 🌟",
  "Add nose, mouth and whiskers":
    "Ajoute le nez, la bouche et les moustaches",
  "Add one big eye and a nostril 👀":
    "Ajoute un grand œil et une narine 👀",
  "Add petals all around! 🌸":
    "Ajoute des pétales tout autour ! 🌸",
  "Add pretty spots! 🌈":
    "Ajoute de jolies taches ! 🌈",
  "Add raindrops falling from the clouds 💧":
    "Ajoute des gouttes de pluie qui tombent des nuages 💧",
  "Add rising bubbles! 🫧":
    "Ajoute des bulles qui montent ! 🫧",
  "Add rosy cheeks and a smile 😊":
    "Ajoute des joues roses et un sourire 😊",
  "Add seeds and grass":
    "Ajoute des graines et de l'herbe",
  "Add shiny hubcaps in the wheels":
    "Ajoute des enjoliveurs brillants dans les roues",
  "Add some big raindrops":
    "Ajoute de grosses gouttes de pluie",
  "Add some yummy apples! 🍎":
    "Ajoute de bonnes pommes ! 🍎",
  "Add sparkles all around! ✨":
    "Ajoute des étincelles tout autour ! ✨",
  "Add sparkles around it ✨":
    "Ajoute des étincelles autour ✨",
  "Add spikes on its back! ⚡":
    "Ajoute des piquants sur son dos ! ⚡",
  "Add straight sunny rays":
    "Ajoute des rayons de soleil tout droits",
  "Add swirly patterns on the wings 🌈":
    "Ajoute des motifs qui tourbillonnent sur les ailes 🌈",
  "Add the back legs 🦵":
    "Ajoute les pattes arrière 🦵",
  "Add the big round shell 🐚":
    "Ajoute la grande coquille ronde 🐚",
  "Add the big top wings":
    "Ajoute les grandes ailes du haut",
  "Add the body":
    "Ajoute le corps",
  "Add the body and collar":
    "Ajoute le corps et le collier",
  "Add the body and outfit":
    "Ajoute le corps et l'habit",
  "Add the body, arms and legs":
    "Ajoute le corps, les bras et les jambes",
  "Add the bottom wings":
    "Ajoute les ailes du bas",
  "Add the collar at the neck 🧵":
    "Ajoute le col au niveau du cou 🧵",
  "Add the cuffs and the shoulder seams ✂️":
    "Ajoute les poignets et les coutures des épaules ✂️",
  "Add the eye mask 🦸":
    "Ajoute le masque 🦸",
  "Add the eye mask and eyes 👀":
    "Ajoute le masque et les yeux 👀",
  "Add the eye, nose and a smile 👁️":
    "Ajoute l'œil, le nez et un sourire 👁️",
  "Add the eyes 👀":
    "Ajoute les yeux 👀",
  "Add the eyes and eyebrows 👀":
    "Ajoute les yeux et les sourcils 👀",
  "Add the family pet dog! 🐕":
    "Ajoute le chien de la famille ! 🐕",
  "Add the first row of raindrops":
    "Ajoute la première rangée de gouttes de pluie",
  "Add the first row of spikes ⚡":
    "Ajoute la première rangée de piquants ⚡",
  "Add the front legs — one prancing! 🦵":
    "Ajoute les pattes avant — une en l'air ! 🦵",
  "Add the front legs and paws 🐾":
    "Ajoute les pattes avant 🐾",
  "Add the gill line and an eye":
    "Ajoute la ligne des ouïes et un œil",
  "Add the hooves and a swishy tail!":
    "Ajoute les sabots et une queue qui balance !",
  "Add the microphone 🎤":
    "Ajoute le micro 🎤",
  "Add the middle of the flower 🟡":
    "Ajoute le milieu de la fleur 🟡",
  "Add the nose, mouth and whiskers":
    "Ajoute le nez, la bouche et les moustaches",
  "Add the pointy cone underneath 🍦":
    "Ajoute le cornet pointu en dessous 🍦",
  "Add the rainbow fringe on top 🌈":
    "Ajoute la frange arc-en-ciel sur le dessus 🌈",
  "Add the road 🛣️":
    "Ajoute la route 🛣️",
  "Add the roof and a chimney 🔺":
    "Ajoute le toit et une cheminée 🔺",
  "Add the round middle 🟡":
    "Ajoute le milieu tout rond 🟡",
  "Add the sitting body":
    "Ajoute le corps assis",
  "Add the stars ⭐":
    "Ajoute les étoiles ⭐",
  "Add the underarm lines and the side seams 🧵":
    "Ajoute les lignes sous les bras et les coutures des côtés 🧵",
  "Add the white parts of the eyes ⚪":
    "Ajoute le blanc des yeux ⚪",
  "Add the windows 🪟":
    "Ajoute les fenêtres 🪟",
  "Add thin red lines between the stripes":
    "Ajoute de fins traits rouges entre les bandes",
  "Add twinkly stars! ⭐":
    "Ajoute des étoiles qui scintillent ! ⭐",
  "Add two big eye bumps on top 👀":
    "Ajoute deux grosses bosses pour les yeux 👀",
  "Add two big eyes 👀":
    "Ajoute deux grands yeux 👀",
  "Add two big floppy ears 👂":
    "Ajoute deux grandes oreilles tombantes 👂",
  "Add two big round ears 👂":
    "Ajoute deux grandes oreilles rondes 👂",
  "Add two big round eyes 👀":
    "Ajoute deux grands yeux ronds 👀",
  "Add two big wheels ⚫":
    "Ajoute deux grandes roues ⚫",
  "Add two ears on the sides 👂":
    "Ajoute deux oreilles sur les côtés 👂",
  "Add two eyes — make them different! 👁️":
    "Ajoute deux yeux — fais-les tout différents ! 👁️",
  "Add two eyes — one round, one from the side! 👁️":
    "Ajoute deux yeux — un rond, un de côté ! 👁️",
  "Add two eyes 👀":
    "Ajoute deux yeux 👀",
  "Add two floppy ears 👂":
    "Ajoute deux oreilles tombantes 👂",
  "Add two lines across":
    "Ajoute deux traits en travers",
  "Add two lines down":
    "Ajoute deux traits vers le bas",
  "Add two little ears 👂":
    "Ajoute deux petites oreilles 👂",
  "Add two little feelers 👀":
    "Ajoute deux petites antennes 👀",
  "Add two little feet 🧡":
    "Ajoute deux petits pieds 🧡",
  "Add two little hooves 🦄":
    "Ajoute deux petits sabots 🦄",
  "Add two little horns 🔶":
    "Ajoute deux petites cornes 🔶",
  "Add two little tusks":
    "Ajoute deux petites défenses",
  "Add two pointy ears 👂":
    "Ajoute deux oreilles pointues 👂",
  "Add two pointy ears 🔺":
    "Ajoute deux oreilles pointues 🔺",
  "Add two pointy ears on top 🔺":
    "Ajoute deux oreilles pointues sur le dessus 🔺",
  "Add two puffy cheeks 😊":
    "Ajoute deux joues toutes rondes 😊",
  "Add two rosy cheeks 😊":
    "Ajoute deux joues toutes roses 😊",
  "Add two round ears on top 👂":
    "Ajoute deux oreilles rondes sur la tête 👂",
  "Add two round eyes 👀":
    "Ajoute deux yeux ronds 👀",
  "Add two rows of curvy scales 🐟":
    "Ajoute deux rangées d'écailles arrondies 🐟",
  "Add two tall ears 👂":
    "Ajoute deux grandes oreilles 👂",
  "Add two tiny nostrils 👃":
    "Ajoute deux toutes petites narines 👃",
  "Add two windows 🪟":
    "Ajoute deux fenêtres 🪟",
  "Add wavy seaweed 🌿":
    "Ajoute des algues qui ondulent 🌿",
  "Add windows with little panes 🪟":
    "Ajoute des fenêtres avec de petits carreaux 🪟",
  "Add yummy apples! 🍎":
    "Ajoute de bonnes pommes ! 🍎",
  "Almost there 🔵":
    "On y est presque 🔵",
  "And one on the right! ☁️":
    "Et un à droite ! ☁️",
  "And that’s little you! 🙂":
    "Et voilà, c'est toi en petit ! 🙂",
  "Another arc just inside 🟠":
    "Un autre arc juste à l'intérieur 🟠",
  "Another one 🟢":
    "Encore un 🟢",
  "Brew a storm cloud on the right! ☁️":
    "Prépare un nuage d'orage à droite ! ☁️",
  "Buttery yellow for the horn! 🌟":
    "Du jaune doré pour la corne ! 🌟",
  "Colour the red trim first! 🖍️":
    "Colorie d'abord le bord rouge ! 🖍️",
  "Draw 4 thin red diagonal stripes ╳":
    "Dessine 4 fines bandes rouges en diagonale ╳",
  "Draw 8 blue triangles in the corners 🔺":
    "Dessine 8 triangles bleus dans les coins 🔺",
  "Draw a big circle in the middle ⭕":
    "Dessine un grand cercle au milieu ⭕",
  "Draw a big diamond 💎":
    "Dessine un grand losange 💎",
  "Draw a big fluffy cloud ☁️":
    "Dessine un grand nuage tout doux ☁️",
  "Draw a big fluffy mane! 🦁":
    "Dessine une grande crinière toute douce ! 🦁",
  "Draw a big head shape 🟣":
    "Dessine une grande forme de tête 🟣",
  "Draw a big red cross ✚":
    "Dessine une grande croix rouge ✚",
  "Draw a big round body 🦕":
    "Dessine un grand corps tout rond 🦕",
  "Draw a big round body! 🐳":
    "Dessine un grand corps tout rond ! 🐳",
  "Draw a big round head! 🐹":
    "Dessine une grosse tête toute ronde ! 🐹",
  "Draw a big round head! 🐻":
    "Dessine une grosse tête toute ronde ! 🐻",
  "Draw a big round penguin body! 🐧":
    "Dessine un grand corps de pingouin tout rond ! 🐧",
  "Draw a big round sun ☀️":
    "Dessine un grand soleil tout rond ☀️",
  "Draw a big star in the middle ⭐":
    "Dessine une grande étoile au milieu ⭐",
  "Draw a big thick red cross ✚":
    "Dessine une grande croix rouge épaisse ✚",
  "Draw a big twinkly star ⭐":
    "Dessine une grande étoile qui scintille ⭐",
  "Draw a big white X corner to corner ╳":
    "Dessine un grand X blanc d'un coin à l'autre ╳",
  "Draw a big yellow triangle 🔺":
    "Dessine un grand triangle jaune 🔺",
  "Draw a circle in the middle ⭕":
    "Dessine un cercle au milieu ⭕",
  "Draw a circle inside ⭕":
    "Dessine un cercle à l'intérieur ⭕",
  "Draw a crescent moon 🌙":
    "Dessine un croissant de lune 🌙",
  "Draw a curvy line down the middle — that splits the face!":
    "Dessine une ligne courbe au milieu — elle coupe le visage en deux !",
  "Draw a five-point star in the middle ⭐":
    "Dessine une étoile à cinq branches au milieu ⭐",
  "Draw a fluffy cloud ☁️":
    "Dessine un nuage tout doux ☁️",
  "Draw a green five-point star ⭐":
    "Dessine une étoile verte à cinq branches ⭐",
  "Draw a line on the beak 〰️":
    "Dessine un trait sur le bec 〰️",
  "Draw a little cactus in the middle 🌵":
    "Dessine un petit cactus au milieu 🌵",
  "Draw a little eagle in the middle 🦅":
    "Dessine un petit aigle au milieu 🦅",
  "Draw a little house behind 🏠":
    "Dessine une petite maison derrière 🏠",
  "Draw a little red emblem in the middle 🌷":
    "Dessine un petit emblème rouge au milieu 🌷",
  "Draw a little white box in the middle":
    "Dessine un petit carré blanc au milieu",
  "Draw a long horse head and neck from the side 🟫":
    "Dessine une longue tête et un long cou de cheval de côté 🟫",
  "Draw a long sword underneath 🗡️":
    "Dessine une longue épée en dessous 🗡️",
  "Draw a maple leaf in the middle 🍁":
    "Dessine une feuille d'érable au milieu 🍁",
  "Draw a palm tree on a hill 🌴":
    "Dessine un palmier sur une colline 🌴",
  "Draw a round badge in the middle":
    "Dessine un écusson rond au milieu",
  "Draw a round head 🟠":
    "Dessine une tête toute ronde 🟠",
  "Draw a round head! 🐘":
    "Dessine une tête toute ronde ! 🐘",
  "Draw a round head! 🐭":
    "Dessine une tête toute ronde ! 🐭",
  "Draw a round head! 🐮":
    "Dessine une tête toute ronde ! 🐮",
  "Draw a round head! 🐰":
    "Dessine une tête toute ronde ! 🐰",
  "Draw a round head! 🐷":
    "Dessine une tête toute ronde ! 🐷",
  "Draw a round head! 🦆":
    "Dessine une tête toute ronde ! 🦆",
  "Draw a round head! 🦊":
    "Dessine une tête toute ronde ! 🦊",
  "Draw a shield and a crown 👑":
    "Dessine un bouclier et une couronne 👑",
  "Draw a shield in the middle 🛡️":
    "Dessine un bouclier au milieu 🛡️",
  "Draw a shield with a bird on top 🛡️":
    "Dessine un bouclier avec un oiseau dessus 🛡️",
  "Draw a sideways green Y across the flag":
    "Dessine un Y vert couché en travers du drapeau",
  "Draw a smiling sun in the box ☀️":
    "Dessine un soleil souriant dans le carré ☀️",
  "Draw a smiling sun in the middle ☀️":
    "Dessine un soleil souriant au milieu ☀️",
  "Draw a star inside a ring ⭐":
    "Dessine une étoile dans un anneau ⭐",
  "Draw a triangle at the left edge 🔺":
    "Dessine un triangle sur le bord gauche 🔺",
  "Draw a triangle from the side 🔺":
    "Dessine un triangle depuis le côté 🔺",
  "Draw a triangle on the left 🔺":
    "Dessine un triangle à gauche 🔺",
  "Draw a twinkly star ⭐":
    "Dessine une étoile qui scintille ⭐",
  "Draw a wheel in the middle ☸️":
    "Dessine une roue au milieu ☸️",
  "Draw a wide round head! 🐸":
    "Dessine une large tête toute ronde ! 🐸",
  "Draw a wide stripe corner to corner":
    "Dessine une large bande d'un coin à l'autre",
  "Draw a wonky face shape 🟦":
    "Dessine un visage tout de travers 🟦",
  "Draw creamy swirls in the mane and tail 🍦":
    "Dessine des mèches crème dans la crinière et la queue 🍦",
  "Draw Daddy 🧔":
    "Dessine papa 🧔",
  "Draw her big flowy dress 👗":
    "Dessine sa grande robe qui flotte 👗",
  "Draw her castle in the distance 🏰":
    "Dessine son château au loin 🏰",
  "Draw her hair fringe":
    "Dessine sa frange",
  "Draw Mommy 👩":
    "Dessine maman 👩",
  "Draw one big star ⭐":
    "Dessine une grande étoile ⭐",
  "Draw the back and the round tummy 🐴":
    "Dessine le dos et le ventre tout rond 🐴",
  "Draw the big curling wave 🌊":
    "Dessine la grande vague qui s'enroule 🌊",
  "Draw the big happy eyes 👀":
    "Dessine les grands yeux joyeux 👀",
  "Draw the big sparkly eyes 👀":
    "Dessine les grands yeux qui brillent 👀",
  "Draw the biggest arc 🔴":
    "Dessine le plus grand arc 🔴",
  "Draw the body and antennae 🦋":
    "Dessine le corps et les antennes 🦋",
  "Draw the body, segments and antennae":
    "Dessine le corps, les anneaux et les antennes",
  "Draw the car body 🚗":
    "Dessine la carrosserie de la voiture 🚗",
  "Draw the door 🚪":
    "Dessine la porte 🚪",
  "Draw the door with a doorknob 🚪":
    "Dessine la porte avec une poignée 🚪",
  "Draw the face and cool hair":
    "Dessine le visage et les cheveux trop cool",
  "Draw the fish body":
    "Dessine le corps du poisson",
  "Draw the fish body 🐟":
    "Dessine le corps du poisson 🐟",
  "Draw the flag's box 🏁":
    "Dessine le cadre du drapeau 🏁",
  "Draw the flowing mane":
    "Dessine la crinière qui flotte",
  "Draw the flowing mane down the neck 💇":
    "Dessine la crinière qui flotte le long du cou 💇",
  "Draw the grassy ground 🌱":
    "Dessine le sol tout herbeux 🌱",
  "Draw the happy eyes 👀":
    "Dessine les yeux joyeux 👀",
  "Draw the head with a soft snout 🐴":
    "Dessine la tête avec un doux museau 🐴",
  "Draw the hem, then start the big number 🔢":
    "Dessine le bas, puis commence le grand chiffre 🔢",
  "Draw the hero's head 🦸":
    "Dessine la tête du héros 🦸",
  "Draw the left side of the heart":
    "Dessine le côté gauche du cœur",
  "Draw the magic horn 🌟":
    "Dessine la corne magique 🌟",
  "Draw the nose and a smiley mouth 👃":
    "Dessine le nez et une bouche souriante 👃",
  "Draw the nose and mouth 👃":
    "Dessine le nez et la bouche 👃",
  "Draw the rolling hills at the bottom 🏞️":
    "Dessine les collines toutes douces en bas 🏞️",
  "Draw the round body and pointy nose 🟤":
    "Dessine le corps tout rond et le nez pointu 🟤",
  "Draw the round ice cream scoop 🍦":
    "Dessine la boule de glace toute ronde 🍦",
  "Draw the shoulders and the sleeves 💪":
    "Dessine les épaules et les manches 💪",
  "Draw the snail's long foot 🐌":
    "Dessine le long pied de l'escargot 🐌",
  "Draw the stem and a leaf 🌿":
    "Dessine la tige et une feuille 🌿",
  "Draw the stem and two leaves 🌿":
    "Dessine la tige et deux feuilles 🌿",
  "Draw the swirl on the shell 🌀":
    "Dessine la spirale sur la coquille 🌀",
  "Draw the trunk 🟫":
    "Dessine le tronc 🟫",
  "Draw the trunk with roots 🟫":
    "Dessine le tronc avec les racines 🟫",
  "Draw the two sides and the bottom of the shirt 👕":
    "Dessine les deux côtés et le bas du maillot 👕",
  "Draw the unicorn's round body 🦄":
    "Dessine le corps tout rond de la licorne 🦄",
  "Draw the waffle lines on the cone 🧇":
    "Dessine les lignes gaufrées sur le cornet 🧇",
  "Draw the walls (a big square) 🟫":
    "Dessine les murs (un grand carré) 🟫",
  "Draw two stars ⭐":
    "Dessine deux étoiles ⭐",
  "Draw two white stars in the corner ⭐":
    "Dessine deux étoiles blanches dans le coin ⭐",
  "Draw your big brother or sister 🧒":
    "Dessine ton grand frère ou ta grande sœur 🧒",
  "Fill it with a checkerboard ▩":
    "Remplis-le d'un damier ▩",
  "Finish the arcs with violet! 🟣":
    "Termine les arcs avec du violet ! 🟣",
  "Finish with a ball of yarn to play with! 🧶":
    "Termine par une pelote de laine pour jouer ! 🧶",
  "Finish with a bright sun! ☀️":
    "Termine par un beau soleil ! ☀️",
  "Finish with a curly tail!":
    "Termine par une queue en tire-bouchon !",
  "Finish with a flower and grass 🌸":
    "Termine par une fleur et de l'herbe 🌸",
  "Finish with a fluffy cloud ☁️":
    "Termine par un nuage tout doux ☁️",
  "Finish with a friendly eye! 😄":
    "Termine par un œil tout gentil ! 😄",
  "Finish with a happy smile! 🦔":
    "Termine par un grand sourire ! 🦔",
  "Finish with a happy smile! 😄":
    "Termine par un grand sourire ! 😄",
  "Finish with a lightning emblem in a shield! ⚡":
    "Termine par un éclair dans un bouclier ! ⚡",
  "Finish with a lightning emblem on the chest! ⚡":
    "Termine par un éclair sur la poitrine ! ⚡",
  "Finish with a row of flowers below! 🌷":
    "Termine par une rangée de fleurs en bas ! 🌷",
  "Finish with a shining sun! ☀️":
    "Termine par un soleil qui brille ! ☀️",
  "Finish with a sparkly star! ⭐":
    "Termine par une étoile qui brille ! ⭐",
  "Finish with a splashy puddle 💧":
    "Termine par une flaque qui éclabousse 💧",
  "Finish with a sun and a path! ☀️":
    "Termine par un soleil et un chemin ! ☀️",
  "Finish with a sun and birds! ☀️🐦":
    "Termine par un soleil et des oiseaux ! ☀️🐦",
  "Finish with a sunny sky! ☀️":
    "Termine par un ciel ensoleillé ! ☀️",
  "Finish with a wagging tail!":
    "Termine par une queue qui remue !",
  "Finish with an apple on its back! 🍎":
    "Termine par une pomme sur son dos ! 🍎",
  "Finish with an eye and a smile! 🐠":
    "Termine par un œil et un sourire ! 🐠",
  "Finish with an eye and spots 😄":
    "Termine par un œil et des taches 😄",
  "Finish with birds flying by! 🐦":
    "Termine par des oiseaux qui passent ! 🐦",
  "Finish with bubbles! 🫧":
    "Termine par des bulles ! 🫧",
  "Finish with cubist lines! 🎨":
    "Termine par des lignes cubistes ! 🎨",
  "Finish with grass at the bottom! 🌱":
    "Termine par de l'herbe en bas ! 🌱",
  "Finish with little boats riding the sea ⛵":
    "Termine par de petits bateaux sur la mer ⛵",
  "Finish with little hearts floating up!":
    "Termine par de petits cœurs qui s'envolent !",
  "Finish with little hooves and a swishy tail!":
    "Termine par de petits sabots et une queue qui balance !",
  "Finish with musical notes and a star! 🎵⭐":
    "Termine par des notes de musique et une étoile ! 🎵⭐",
  "Finish with musical notes! 🎵":
    "Termine par des notes de musique ! 🎵",
  "Finish with swirly wing patterns! 🌈":
    "Termine par des motifs qui tourbillonnent sur les ailes ! 🌈",
  "Finish with the ground and a bone! 🦴":
    "Termine par le sol et un os ! 🦴",
  "Finish with the ground and a fern 🌿":
    "Termine par le sol et une fougère 🌿",
  "Finish with the road! 🛣️":
    "Termine par la route ! 🛣️",
  "Finish with the sun! ☀️":
    "Termine par le soleil ! ☀️",
  "Finish with the tall cypress tree 🌲":
    "Termine par le grand cyprès 🌲",
  "Finish with two little hearts!":
    "Termine par deux petits cœurs !",
  "Finish with whiskers and a smile! 😺":
    "Termine par des moustaches et un sourire ! 😺",
  "Finish with wild hair and an ear! 🎨":
    "Termine par des cheveux en bataille et une oreille ! 🎨",
  "Flow the wavy mane down the neck 🌊":
    "Fais onduler la crinière le long du cou 🌊",
  "Give her a happy smile 😊":
    "Donne-lui un grand sourire 😊",
  "Give it a cute nose 👃":
    "Donne-lui un joli petit nez 👃",
  "Give it a flowing tail 🌈":
    "Donne-lui une queue qui flotte 🌈",
  "Give it a happy face! 😊":
    "Donne-lui un visage tout joyeux ! 😊",
  "Give it an eye and a nose 👁️":
    "Donne-lui un œil et un nez 👁️",
  "Give it four chunky legs and feet":
    "Donne-lui 4 grosses pattes et des pieds",
  "Give the number its 3-D edge 🔢":
    "Donne au chiffre son bord en 3-D 🔢",
  "Give the pony a saddle 🏇":
    "Donne une selle au poney 🏇",
  "Give the sun a happy face 😊":
    "Donne au soleil un visage tout joyeux 😊",
  "Give the sun a happy face 🙂":
    "Donne un visage joyeux au soleil 🙂",
  "Green next 🟢":
    "Le vert ensuite 🟢",
  "Hang a little swing from a branch 🪢":
    "Accroche une petite balançoire à une branche 🪢",
  "Indigo after that":
    "L'indigo après",
  "Keep going 🟡":
    "Continue 🟡",
  "Last of all, write the dog's name: D-O-G! ✏️":
    "Pour finir, écris le nom du chien : D-O-G ! ✏️",
  "Lilac swirls next! 💜":
    "Ensuite les mèches lilas ! 💜",
  "Make a little flag box in the corner":
    "Fais un petit carré de drapeau dans le coin",
  "Make a sunny meadow with grass ☀️":
    "Fais un pré ensoleillé avec de l'herbe ☀️",
  "Now bring it to life with color! 🎨":
    "Maintenant, donne-lui vie avec de la couleur ! 🎨",
  "Now color her all in! 🖍️":
    "Maintenant, colorie-la toute entière ! 🖍️",
  "Now color him all in! 🖍️":
    "Maintenant, colorie-le tout entier ! 🖍️",
  "Now color it all in! 🖍️":
    "Maintenant, colorie-le tout entier ! 🖍️",
  "Now color it in! 🖍️":
    "Maintenant, colorie-le ! 🖍️",
  "Now color the bear in! 🖍️":
    "Maintenant, colorie l'ours ! 🖍️",
  "Now color the bunny in! 🖍️":
    "Maintenant, colorie le lapin ! 🖍️",
  "Now color the cloud in! 🖍️":
    "Maintenant, colorie le nuage ! 🖍️",
  "Now color the cow in! 🖍️":
    "Maintenant, colorie la vache ! 🖍️",
  "Now color the duck in! 🖍️":
    "Maintenant, colorie le canard ! 🖍️",
  "Now color the elephant in! 🖍️":
    "Maintenant, colorie l'éléphant ! 🖍️",
  "Now color the fish in! 🖍️":
    "Maintenant, colorie le poisson ! 🖍️",
  "Now color the fox in! 🖍️":
    "Maintenant, colorie le renard ! 🖍️",
  "Now color the frog in! 🖍️":
    "Maintenant, colorie la grenouille ! 🖍️",
  "Now color the hamster in! 🖍️":
    "Maintenant, colorie le hamster ! 🖍️",
  "Now color the horse in! 🖍️":
    "Maintenant, colorie le cheval ! 🖍️",
  "Now color the lion in! 🖍️":
    "Maintenant, colorie le lion ! 🖍️",
  "Now color the mouse in! 🖍️":
    "Maintenant, colorie la souris ! 🖍️",
  "Now color the penguin in! 🖍️":
    "Maintenant, colorie le pingouin ! 🖍️",
  "Now color the pig in! 🖍️":
    "Maintenant, colorie le cochon ! 🖍️",
  "Now color the whale in! 🖍️":
    "Maintenant, colorie la baleine ! 🖍️",
  "Now color the whole rainbow in! 🌈":
    "Maintenant, colorie tout l'arc-en-ciel ! 🌈",
  "Now color the whole sky in! 🌈":
    "Maintenant, colorie tout le ciel ! 🌈",
  "Now colour the flag in! 🖍️":
    "Maintenant, colorie le drapeau ! 🖍️",
  "Now colour the shirt blue! 🖍️":
    "Maintenant, colorie le maillot en bleu ! 🖍️",
  "Now colour your circle in! 🖍️":
    "Maintenant, colorie ton cercle ! 🖍️",
  "Now colour your crescent in! 🖍️":
    "Maintenant, colorie ton croissant ! 🖍️",
  "Now colour your diamond in! 🖍️":
    "Maintenant, colorie ton losange ! 🖍️",
  "Now colour your family in! 🖍️":
    "Maintenant, colorie ta famille ! 🖍️",
  "Now colour your family scene in! 🖍️":
    "Maintenant, colorie ta scène de famille ! 🖍️",
  "Now colour your heart in! 🖍️":
    "Maintenant, colorie ton cœur ! 🖍️",
  "Now colour your hexagon in! 🖍️":
    "Maintenant, colorie ton hexagone ! 🖍️",
  "Now colour your ice cream in! 🖍️":
    "Maintenant, colorie ta glace ! 🖍️",
  "Now colour your octagon in! 🖍️":
    "Maintenant, colorie ton octogone ! 🖍️",
  "Now colour your oval in! 🖍️":
    "Maintenant, colorie ton ovale ! 🖍️",
  "Now colour your parallelogram in! 🖍️":
    "Maintenant, colorie ton parallélogramme ! 🖍️",
  "Now colour your pentagon in! 🖍️":
    "Maintenant, colorie ton pentagone ! 🖍️",
  "Now colour your rectangle in! 🖍️":
    "Maintenant, colorie ton rectangle ! 🖍️",
  "Now colour your snail in! 🖍️":
    "Maintenant, colorie ton escargot ! 🖍️",
  "Now colour your square in! 🖍️":
    "Maintenant, colorie ton carré ! 🖍️",
  "Now colour your star in! 🖍️":
    "Maintenant, colorie ton étoile ! 🖍️",
  "Now colour your trapezium in! 🖍️":
    "Maintenant, colorie ton trapèze ! 🖍️",
  "Now colour your triangle in! 🖍️":
    "Maintenant, colorie ton triangle ! 🖍️",
  "Now colour your unicorn in! 🖍️":
    "Maintenant, colorie ta licorne ! 🖍️",
  "Now creamy peach swirls! 🍑":
    "Maintenant les mèches pêche ! 🍑",
  "Now draw two big eyes 👀":
    "Maintenant, dessine deux grands yeux 👀",
  "Now mirror it on the right! ❤️":
    "Maintenant, fais pareil à droite ! ❤️",
  "Now orange 🟠":
    "Maintenant l'orange 🟠",
  "Now two sparkly eyes 👀":
    "Maintenant deux yeux qui brillent 👀",
  "Pink for the mane! 🩷":
    "Du rose pour la crinière ! 🩷",
  "Plant a tree beside the house 🌳":
    "Plante un arbre à côté de la maison 🌳",
  "Put the star on a stage with a spotlight! 🔦":
    "Mets la star sur une scène avec un projecteur ! 🔦",
  "Shoot an arrow through it! 🏹":
    "Fais passer une flèche au travers ! 🏹",
  "Split it into four boxes":
    "Divise-le en quatre carrés",
  "Spread a magical wing ✨":
    "Déploie une aile magique ✨",
  "Spread two magical wings ✨":
    "Déploie deux ailes magiques ✨",
  "Sprinkle some sparkles ✨":
    "Saupoudre quelques étincelles ✨",
  "Start with a big round head 🟤":
    "Commence par une grosse tête toute ronde 🟤",
  "Start with a big round head! 🟤":
    "Commence par une grosse tête toute ronde ! 🟤",
  "Start with a big rounded body":
    "Commence par un grand corps tout rond",
  "Start with a red arc 🔴":
    "Commence par un arc rouge 🔴",
  "Start with the pony's body":
    "Commence par le corps du poney",
  "Start with the round head":
    "Commence par la tête toute ronde",
  "Sweep the fringe around the horn 💇":
    "Balaie la frange autour de la corne 💇",
  "Swish a big wavy tail 🌊":
    "Dessine une grande queue qui ondule 🌊",
  "The littlest arc 🟣":
    "Le tout petit arc 🟣",
  "Then blue 🔵":
    "Puis le bleu 🔵",
  "Then yellow 🟡":
    "Puis le jaune 🟡",
  "This is a CIRCLE! Draw one big round circle ⭕":
    "Voici un CERCLE ! Dessine un grand rond ⭕",
  "This is a CRESCENT! The curvy shape of the moon 🌙":
    "Voici un CROISSANT ! La forme courbe de la lune 🌙",
  "This is a DIAMOND! A square standing on its point 💎":
    "Voici un LOSANGE ! Un carré posé sur sa pointe 💎",
  "This is a HEART! Draw two bumps and a point ❤️":
    "Voici un CŒUR ! Dessine deux bosses et une pointe ❤️",
  "This is a HEXAGON! Draw 6 straight sides ⬡":
    "Voici un HEXAGONE ! Dessine 6 côtés droits ⬡",
  "This is a PARALLELOGRAM! A rectangle that leans over ▱":
    "Voici un PARALLÉLOGRAMME ! Un rectangle qui se penche ▱",
  "This is a PENTAGON! Draw 5 straight sides ⬠":
    "Voici un PENTAGONE ! Dessine 5 côtés droits ⬠",
  "This is a RECTANGLE! 2 long sides and 2 short sides ▭":
    "Voici un RECTANGLE ! 2 longs côtés et 2 côtés courts ▭",
  "This is a SQUARE! Draw 4 sides that are all the same 🟦":
    "Voici un CARRÉ ! Dessine 4 côtés tous pareils 🟦",
  "This is a STAR! Draw 5 pointy points ⭐":
    "Voici une ÉTOILE ! Dessine 5 pointes bien piquantes ⭐",
  "This is a TRAPEZIUM! Narrow on top, wide at the bottom ⏢":
    "Voici un TRAPÈZE ! Étroit en haut, large en bas ⏢",
  "This is a TRIANGLE! Draw 3 straight sides 🔺":
    "Voici un TRIANGLE ! Dessine 3 côtés droits 🔺",
  "This is an OCTAGON! Draw 8 straight sides 🛑":
    "Voici un OCTOGONE ! Dessine 8 côtés droits 🛑",
  "This is an OVAL! Like a circle, but stretched 🥚":
    "Voici un OVALE ! Comme un cercle, mais étiré 🥚",
  "Trace the black border ⬛":
    "Trace le contour noir ⬛",
  "Trace the black borders ⬛":
    "Trace les contours noirs ⬛",
  "Write some white marks across ✍️":
    "Écris des marques blanches en travers ✍️",
  "Write the player's name — MBAPPÉ ⚽":
    "Écris le nom du joueur — MBAPPÉ ⚽",
  "Write the words \"Ordem e Progresso\" — \"Order and Progress\"! ✍️":
    "Écris les mots « Ordem e Progresso » — « Ordre et Progrès » ! ✍️",
  "Write three little green marks ✍️":
    "Écris trois petites marques vertes ✍️",
};

/** The "write the name" hint, built from the already-spelt-out letters. */
export const nameHintFr = (spelt: string) => `Enfin, écris le nom : ${spelt} ! ✏️`;

/** Extra spoken words that select a drawing, in French. */
export const ALIASES_FR: Record<string, string[]> = {
  "bear": ["ours"],
  "bunny": ["lapin", "petit lapin"],
  "butterfly": ["papillon"],
  "butterfly-10": ["papillon"],
  "butterfly-7": ["papillon"],
  "car": ["voiture"],
  "car-10": ["voiture"],
  "car-7": ["voiture"],
  "cat": ["chat", "chaton", "minou"],
  "cat-10": ["chat", "chaton", "minou"],
  "cat-7": ["chat", "chaton", "minou"],
  "cloud": ["nuage", "nuage de pluie", "pluie"],
  "cloud-10": ["nuage", "nuage de pluie", "pluie"],
  "cloud-7": ["nuage", "nuage de pluie", "pluie"],
  "cow": ["vache"],
  "cubist-face": ["visage cubiste"],
  "dinosaur": ["dino", "dinosaure", "t rex", "tyrannosaure"],
  "dinosaur-10": ["dino", "dinosaure", "t rex", "tyrannosaure"],
  "dinosaur-7": ["dino", "dinosaure", "t rex", "tyrannosaure"],
  "dinosaur-blue": ["dino", "dinosaure", "t rex", "tyrannosaure"],
  "dog": ["chien", "chiot", "toutou"],
  "dog-10": ["chien", "chiot", "toutou"],
  "dog-7": ["chien", "chiot", "toutou"],
  "duck": ["canard"],
  "elephant": ["elephant", "éléphant"],
  "family": ["famille"],
  "family-10": ["famille"],
  "family-7": ["famille"],
  "fish": ["poisson"],
  "fish-10": ["poisson"],
  "fish-7": ["poisson"],
  "flag-algeria": ["algerie", "algérie"],
  "flag-argentina": ["argentine"],
  "flag-australia": ["australie"],
  "flag-austria": ["autriche"],
  "flag-belgium": ["belgique"],
  "flag-bosnia-and-herzegovina": ["bosnie-herzegovine", "bosnie-herzégovine"],
  "flag-brazil": ["bresil", "brésil"],
  "flag-canada": ["canada"],
  "flag-cape-verde": ["cap-vert"],
  "flag-china": ["chine"],
  "flag-colombia": ["colombie"],
  "flag-croatia": ["croatie"],
  "flag-curacao": ["curacao", "curaçao"],
  "flag-czech-republic": ["republique tcheque", "république tchèque"],
  "flag-denmark": ["danemark"],
  "flag-dr-congo": ["rd congo"],
  "flag-ecuador": ["equateur", "équateur"],
  "flag-egypt": ["egypte", "égypte"],
  "flag-england": ["angleterre"],
  "flag-finland": ["finlande"],
  "flag-france": ["france"],
  "flag-germany": ["allemagne"],
  "flag-ghana": ["ghana"],
  "flag-greece": ["grece", "grèce"],
  "flag-haiti": ["haiti", "haïti"],
  "flag-india": ["inde"],
  "flag-iran": ["iran"],
  "flag-iraq": ["irak"],
  "flag-ireland": ["irlande"],
  "flag-italy": ["italie"],
  "flag-ivory-coast": ["cote d'ivoire", "côte d'ivoire"],
  "flag-japan": ["japon"],
  "flag-jordan": ["jordanie"],
  "flag-mexico": ["mexique"],
  "flag-morocco": ["maroc"],
  "flag-netherlands": ["pays-bas"],
  "flag-new-zealand": ["nouvelle-zelande", "nouvelle-zélande"],
  "flag-norway": ["norvege", "norvège"],
  "flag-panama": ["panama"],
  "flag-paraguay": ["paraguay"],
  "flag-portugal": ["portugal"],
  "flag-qatar": ["qatar"],
  "flag-saudi-arabia": ["arabie saoudite"],
  "flag-scotland": ["ecosse", "écosse"],
  "flag-senegal": ["senegal", "sénégal"],
  "flag-singapore": ["singapour"],
  "flag-south-africa": ["afrique du sud"],
  "flag-south-korea": ["coree du sud", "corée du sud"],
  "flag-spain": ["espagne"],
  "flag-sweden": ["suede", "suède"],
  "flag-switzerland": ["suisse"],
  "flag-tunisia": ["tunisie"],
  "flag-turkey": ["turquie"],
  "flag-united-arab-emirates": ["emirats arabes unis", "émirats arabes unis"],
  "flag-united-kingdom": ["royaume-uni"],
  "flag-united-states": ["etats-unis", "états-unis"],
  "flag-uruguay": ["uruguay"],
  "flag-uzbekistan": ["ouzbekistan", "ouzbékistan"],
  "flower": ["fleur"],
  "flower-10": ["fleur"],
  "flower-7": ["fleur"],
  "fox": ["renard"],
  "frog": ["grenouille"],
  "great-wave": ["grande vague"],
  "hamster": ["hamster"],
  "heart": ["cœur"],
  "heart-10": ["cœur"],
  "heart-7": ["cœur"],
  "hedgehog": ["herisson", "hérisson"],
  "hedgehog-10": ["herisson", "hérisson"],
  "hedgehog-7": ["herisson", "hérisson"],
  "heromask": ["heros", "super heros", "super stormy", "super-stormy", "superheros"],
  "heromask-10": ["heros", "super heros", "super stormy", "super-stormy", "superheros"],
  "heromask-7": ["heros", "super heros", "super stormy", "super-stormy", "superheros"],
  "horse": ["cheval", "poney"],
  "horse-10": ["cheval", "poney"],
  "horse-7": ["cheval", "poney"],
  "house": ["maison"],
  "house-10": ["maison"],
  "house-7": ["maison"],
  "ice-cream": ["creme glacee", "glace"],
  "jersey": ["maillot"],
  "unicorn-10": ["licorne", "licorne volante", "pegase", "pégase"],
  "lion": ["lion"],
  "mouse": ["souris"],
  "penguin": ["pingouin"],
  "picasso-portrait": ["portrait a la picasso", "portrait à la picasso"],
  "pig": ["cochon"],
  "popstar": ["chanteuse", "pop star"],
  "popstar-10": ["chanteuse", "pop star"],
  "popstar-7": ["chanteuse", "pop star"],
  "princess": ["princesse"],
  "princess-10": ["princesse"],
  "princess-7": ["princesse"],
  "rainbow": ["arc-en-ciel"],
  "rainbow-10": ["arc-en-ciel"],
  "rainbow-7": ["arc-en-ciel"],
  "shape-circle": ["cercle", "rond"],
  "shape-crescent": ["croissant"],
  "shape-diamond": ["losange"],
  "shape-heart": ["cœur"],
  "shape-hexagon": ["hexagone"],
  "shape-octagon": ["octogone"],
  "shape-oval": ["ovale"],
  "shape-parallelogram": ["parallelogramme", "parallélogramme"],
  "shape-pentagon": ["pentagone"],
  "shape-rectangle": ["rectangle"],
  "shape-square": ["carre", "carré"],
  "shape-star": ["etoile", "étoile"],
  "shape-trapezium": ["trapeze", "trapèze"],
  "shape-triangle": ["triangle"],
  "snail": ["escargot"],
  "star": ["etoile", "étoile"],
  "star-10": ["etoile", "étoile"],
  "star-7": ["etoile", "étoile"],
  "starry-night": ["nuit etoilee", "nuit étoilée"],
  "sun": ["soleil"],
  "sun-10": ["soleil"],
  "sun-7": ["soleil"],
  "tree": ["arbre"],
  "tree-10": ["arbre"],
  "tree-7": ["arbre"],
  "whale": ["baleine"],
};
